// Modules
import React from 'react';

// Material-ui
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';

// Helpers
// Components
// Common components

type AlertSnackbarProps = {
  handleClose: () => void;
  message: string | React.ReactNode | ((params: { close: () => void }) => React.ReactNode);
  alertSeverity: 'info' | 'warning' | 'error' | 'success';
  action?: {
    label: string;
    onClick: (params: { close: () => void }) => void;
    icon?: React.ReactNode;
  };
  open?: boolean;
  anchorOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  sx?: any;
};

const processChildren = (children: React.ReactNode): React.ReactNode => {
  return React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) {
      return child;
    }
    const element = child as React.ReactElement<any>;
    const newProps: React.PropsWithChildren<Record<string, unknown>> = {};

    // For MUI Links, we want to ensure consistent styling within alerts.
    if (element.type === Link) {
      // Default to inheriting color to match alert text, unless specified.
      newProps.color = element.props.color ?? 'inherit';
      // Default to 'body2' which matches the Alert's typography.
      newProps.variant = element.props.variant ?? 'body2';
    }

    // Recurse through children
    if (element.props.children) {
      newProps.children = processChildren(element.props.children);
    }

    if (Object.keys(newProps).length > 0) {
      return React.cloneElement(element, { ...element.props, ...newProps });
    }

    return element;
  });
};

/**
 * **IMPORTANT**: Avoid using this component directly. Use the `showAlertSnackbarAtom` atom for React components or `showAlertSnackbar` function for non-React code instead.
 */
export default function AlertSnackbar({
  handleClose,
  message,
  alertSeverity,
  action,
  ...props
}: AlertSnackbarProps) {
  const messageNode = typeof message === 'function' ? message({ close: handleClose }) : message;
  const processedMessage = processChildren(messageNode);

  return (
    <Snackbar onClose={handleClose} {...props}>
      <Alert
        onClose={handleClose}
        severity={alertSeverity}
        sx={{
          width: '100%',
          // Fix center aligment of the action button
          '& .MuiAlert-action': {
            alignItems: 'center',
            paddingTop: 0,
          },
          '& .MuiAlert-icon': {
            alignSelf: 'center',
          },
        }}
        action={
          action ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => action.onClick({ close: handleClose })}
              sx={{ ml: 2 }}
              startIcon={action.icon}
            >
              {action.label}
            </Button>
          ) : null
        }
      >
        {processedMessage}
      </Alert>
    </Snackbar>
  );
}
